import apiClient from "@/api/apiClient";
import { backendClient } from "@/api/backendClient";
import { trackPosthogEvent } from "@/utils/posthog";

import type {
  CaseExportQueryParams,
  CaseExtended,
  CaseInsert,
  CaseListQueryParams,
  CaseUpdate,
  EntityTreeNode,
} from "@waku-care/types/contracts/internal";

import type { Case } from "@/supabase.types";

import type { SearchCasesProps } from "./types";

export interface ListCasesByWorkspaceIdProps {
  workspace_id?: string;
  params?: CaseListQueryParams;
}

export const getCase = async (id: string) => {
  const { data, error } = await backendClient.get<CaseExtended>(`/cases/${id}`);
  if (error) throw error;
  return data;
};

export const getCasesByIds = async (ids: string[]) => {
  if (ids.length === 0) return [];
  const { data, error } = await backendClient.get<CaseExtended[]>(`/cases/by-ids`, { params: { ids } });
  if (error) throw error;
  return data;
};

export const getCasesByWorkspaceId = async ({ workspace_id, params }: Required<ListCasesByWorkspaceIdProps>) => {
  const { data, count, error } = await backendClient.get<CaseExtended[]>(`/cases/by-workspace/${workspace_id}`, {
    params,
  });
  if (error) throw error;
  return { data, count };
};

export const createCase = async (values: CaseInsert) => {
  const { data, error } = await backendClient.post<CaseExtended>(`/cases`, { body: values });
  if (error) throw error;
  return data;
};

export const updateCase = async ({ id, values }: { id: string; values: CaseUpdate }) => {
  const { data, error } = await backendClient.patch<CaseExtended>(`/cases/${id}`, { body: values });
  if (error) throw error;
  return data;
};

export const getCaseTree = async (id: string) => {
  const { data, error } = await backendClient.get<EntityTreeNode[]>(`/cases/${id}/tree`);
  if (error) throw error;
  return data;
};

// CSV export of the workspace's cases, as CSV text
export const exportCasesCsv = async ({
  workspace_id,
  params,
}: {
  workspace_id: string;
  params: CaseExportQueryParams;
}) => {
  const { data, error } = await backendClient.get<string>(`/cases/export/by-workspace/${workspace_id}`, { params });
  if (error) throw error;
  return data;
};

export const searchCases = async ({
  query: _query,
  workspace_ids,
  max_results,
  manufacturer_id,
  device_model_id,
  device_id,
  withPosthogTracking = true,
}: SearchCasesProps): Promise<Case[]> => {
  const response = await apiClient.functions.invoke<{ success: boolean; responseParams: Case[] }>("search", {
    body: {
      query: _query,
      entity: "cases",
      workspace_ids,
      max_results,
      manufacturer_id: manufacturer_id === "" ? null : manufacturer_id,
      device_model_id: device_model_id === "" ? null : device_model_id,
      device_id: device_id === "" ? null : device_id,
    },
  });
  if (response.error) throw response.error;
  if (!response.data?.success) return [];

  if (withPosthogTracking) {
    trackPosthogEvent("omni search | cases", "cases", {
      search: _query,
      device_model_id,
      manufacturer_id,
      device_id,
      workspace_ids,
      max_results,
    });
  }

  return response.data.responseParams;
};
